import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { portalFetch } from '../lib/supabase';
import SquareCatalogMap from '../components/SquareCatalogMap';
import CloverCatalogMap from '../components/CloverCatalogMap';
import ModifierEditModal from '../components/ModifierEditModal';
function fmtMoney(n) { return `$${(n || 0).toFixed(2)}`; }

function Card({ children, style }) {
  return <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, ...style }}>{children}</div>;
}

const inputStyle = {
  width: '100%', padding: '0.65rem 0.9rem',
  border: '1.5px solid var(--border)', borderRadius: 8,
  fontSize: '0.875rem', outline: 'none',
  color: 'var(--ink)', background: 'white',
};
// Fixed dietary vocabulary -- mirrors api/ingest/parse.js's PARSE_SYSTEM_PROMPT
// dietary_tags list exactly, so AI-parsed and manually-edited dishes stay
// consistent with each other rather than drifting into two separate vocabularies.
const DIETARY_TAG_OPTIONS = ['vegan', 'vegetarian', 'gluten-free', 'halal', 'kosher', 'dairy-free', 'nut-free', 'spicy', 'plant-based'];
// Cuisine tags are deliberately NOT a closed list -- unlike dietary tags, these
// grow as the restaurant roster grows (confirmed via a direct DB query: only
// West African/Nigerian/Ghanaian exist today, since that's the current roster).
// Shown as quick-select checkboxes; the "Add a new cuisine tag" input lets an
// owner add one not yet in this starting list.
const CUISINE_TAG_OPTIONS = ['West African', 'Nigerian', 'Ghanaian'];
// Matches the taxonomy used by both classifyDishRoles() (the existing
// AI backfill system) and the AI parser's dish_role output -- one
// consistent vocabulary across all three places a dish's role can be
// set or changed.
const DISH_ROLE_OPTIONS = ['meal', 'accompaniment', 'beverage', 'dessert'];

const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const blobToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload  = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

const compressImage = (file) => new Promise((resolve) => {
  const canvas = document.createElement('canvas');
  const img    = new Image();
  img.onload = () => {
    const MAX = 1600;
    let { width, height } = img;
    if (width > MAX || height > MAX) {
      if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
      else { width = Math.round(width * MAX / height); height = MAX; }
    }
    canvas.width  = width;
    canvas.height = height;
    canvas.getContext('2d').drawImage(img, 0, 0, width, height);
    canvas.toBlob(resolve, 'image/jpeg', 0.88);
  };
  img.src = URL.createObjectURL(file);
});

// ── DELETE MENU CARD ──────────────────────────────────────────────────────────
function DeleteMenuCard({ onDeleted }) {
  const [confirming, setConfirming] = useState(false);
  const [deleting, setDeleting]     = useState(false);
  const [result, setResult]         = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const res = await portalFetch('/delete-menu', { method: 'DELETE' });
      if (res?.ok) {
        setResult(`Deleted ${res.dishesDeleted} dishes and ${res.categoriesDeleted} categories.`);
        setConfirming(false);
        onDeleted();
      } else {
        setResult(`Error: ${res?.error || 'Delete failed'}`);
      }
    } catch (err) {
      setResult(`Error: ${err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Card style={{ marginBottom: '1rem', border: '1px solid #FCA5A5' }}>
      <div style={{ padding: '1rem 1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#991B1B', marginBottom: 2 }}>🗑️ Delete entire menu</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Permanently removes all dishes and categories. Cannot be undone.</div>
          {result && <div style={{ fontSize: '0.78rem', color: result.startsWith('Error') ? '#991B1B' : '#065F46', marginTop: 4 }}>{result}</div>}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          {!confirming ? (
            <button onClick={() => setConfirming(true)}
              style={{ background: '#FEE2E2', color: '#991B1B', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
              Delete menu
            </button>
          ) : (
            <>
              <button onClick={() => setConfirming(false)}
                style={{ background: 'white', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={handleDelete} disabled={deleting}
                style={{ background: '#DC2626', color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', opacity: deleting ? 0.6 : 1 }}>
                {deleting ? 'Deleting...' : 'Yes, delete everything'}
              </button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
}

// ── ADD MENU CARD ─────────────────────────────────────────────────────────────
function AddMenuCard({ onSuccess }) {
  const [open, setOpen]           = useState(false);
  const [inputType, setInputType] = useState('url');
  const [menuText, setMenuText]   = useState('');
  const [menuUrl, setMenuUrl]     = useState('');
  const [menuFile, setMenuFile]   = useState(null);
  const [fileError, setFileError] = useState('');
  const [status, setStatus]       = useState('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const fileRef = useRef();

  const handleFileChange = (file) => {
    setFileError('');
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'application/pdf'].includes(file.type)) {
      setFileError('Please upload a PDF, JPEG, PNG, or WebP file.'); return;
    }
    if (file.size > 10 * 1024 * 1024) { setFileError('File must be under 10MB.'); return; }
    setMenuFile(file);
    setInputType(file.type === 'application/pdf' ? 'pdf' : 'image');
  };

  const reset = () => {
    setMenuText(''); setMenuUrl(''); setMenuFile(null);
    setFileError(''); setStatus('idle'); setStatusMsg('');
  };

  const handleUpload = async () => {
    if (inputType === 'text' && !menuText.trim()) { setStatusMsg('Please paste your menu text.'); setStatus('error'); return; }
    if (inputType === 'url' && !menuUrl.trim()) { setStatusMsg('Please enter your menu URL.'); setStatus('error'); return; }
    if ((inputType === 'pdf' || inputType === 'image') && !menuFile) { setStatusMsg('Please upload a file.'); setStatus('error'); return; }

    setStatus('uploading');

    try {
      let body;

      if (inputType === 'pdf') {
        setStatusMsg('Uploading PDF...');
        const fileBase64 = await blobToBase64(menuFile);
        const prepResult = await portalFetch('/prepare-menu-upload', {
          method: 'POST',
          body:   JSON.stringify({ fileBase64, contentType: menuFile.type }),
        });
        if (!prepResult?.path) throw new Error(prepResult?.error || 'File upload failed');
        setStatusMsg('Parsing your menu with AI — this takes 15–30 seconds...');
        body = JSON.stringify({ menuInputType: 'storage', storagePath: prepResult.path });

      } else if (inputType === 'image') {
        setStatusMsg('Compressing and parsing menu...');
        const compressed   = await compressImage(menuFile);
        const imageBase64  = await blobToBase64(compressed);
        body = JSON.stringify({ menuInputType: 'image', menuFileBase64: imageBase64, menuFileMime: 'image/jpeg' });

      } else {
        setStatusMsg(inputType === 'url' ? 'Fetching and parsing menu — this takes 15–30 seconds...' : 'Parsing your menu with AI — this takes 15–30 seconds...');
        body = JSON.stringify({
          menuInputType: inputType,
          menuText:      inputType === 'text' ? menuText : null,
          menuSourceUrl: inputType === 'url'  ? menuUrl  : null,
        });
      }

      const result = await portalFetch('/menu-upload', { method: 'POST', body });

      if (result?.ok) {
        setStatus('done');
        setStatusMsg(result.message);
        onSuccess();
      } else {
        throw new Error(result?.error || 'Upload failed');
      }
    } catch (err) {
      setStatus('error');
      setStatusMsg(err.message || 'Something went wrong. Please try again.');
    }
  };

  return (
    <Card style={{ marginBottom: '1.5rem', overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ padding: '1rem 1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', background: open ? 'var(--warm)' : 'white', borderBottom: open ? '1px solid var(--border)' : 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
          <span style={{ fontSize: '1.1rem' }}>📤</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>Add menu</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--muted)' }}>Import dishes from a URL, PDF, photo, or paste text</div>
          </div>
        </div>
        <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>{open ? '▲' : '▾'}</span>
      </div>

      {open && (
        <div style={{ padding: '1.2rem 1.4rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.6rem' }}>Menu format</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem' }}>
              {[
                { type: 'url',   label: '🔗 Menu URL' },
                { type: 'pdf',   label: '📄 Upload PDF' },
                { type: 'image', label: '📷 Upload photo' },
                { type: 'text',  label: '📋 Paste text' },
              ].map(opt => (
                <button key={opt.type} onClick={() => { setInputType(opt.type); setMenuFile(null); setFileError(''); }}
                  style={{ padding: '0.6rem', borderRadius: 8, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                    border: `2px solid ${inputType === opt.type ? 'var(--orange)' : 'var(--border)'}`,
                    background: inputType === opt.type ? '#FFF0E6' : 'white',
                    color: inputType === opt.type ? 'var(--orange)' : 'var(--ink)' }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {inputType === 'url' && (
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: '0.4rem' }}>Menu page URL</label>
              <input type="url" value={menuUrl} onChange={e => setMenuUrl(e.target.value)}
                style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', outline: 'none' }}
                placeholder="https://yourrestaurant.com/menu" />
              <p style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>We'll extract dish names, descriptions, and prices automatically.</p>
            </div>
          )}

          {inputType === 'text' && (
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: '0.4rem' }}>Paste menu text</label>
              <textarea value={menuText} onChange={e => setMenuText(e.target.value)} rows={8}
                style={{ width: '100%', padding: '0.65rem 0.9rem', border: '1.5px solid var(--border)', borderRadius: 8, fontSize: '0.875rem', outline: 'none', resize: 'vertical', fontFamily: 'monospace' }}
                placeholder="Paste your full menu — dish names, descriptions, and prices..." />
            </div>
          )}

          {(inputType === 'pdf' || inputType === 'image') && (
            <div>
              <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'var(--ink)', display: 'block', marginBottom: '0.4rem' }}>
                {inputType === 'pdf' ? 'Upload PDF menu' : 'Upload menu photo'}
              </label>
              <div onClick={() => fileRef.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); handleFileChange(e.dataTransfer.files[0]); }}
                style={{ border: '2px dashed var(--border)', borderRadius: 8, padding: '1.5rem', textAlign: 'center', cursor: 'pointer' }}>
                <input ref={fileRef} type="file" style={{ display: 'none' }}
                  accept={inputType === 'pdf' ? 'application/pdf' : 'image/jpeg,image/png,image/webp'}
                  onChange={e => handleFileChange(e.target.files[0])} />
                {menuFile ? (
                  <><div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#065F46' }}>✓ {menuFile.name}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>{(menuFile.size / 1024 / 1024).toFixed(1)} MB</div></>
                ) : (
                  <><div style={{ fontSize: '0.875rem', color: 'var(--muted)', fontWeight: 500 }}>Click to upload or drag and drop</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginTop: 4 }}>{inputType === 'pdf' ? 'PDF up to 10MB' : 'JPEG, PNG or WebP up to 10MB'}</div></>
                )}
              </div>
              {fileError && <p style={{ fontSize: '0.8rem', color: '#991B1B', marginTop: 6 }}>{fileError}</p>}
            </div>
          )}

          {status === 'uploading' && <div style={{ background: '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#0C447C' }}>⏳ {statusMsg}</div>}
          {status === 'done'     && <div style={{ background: '#D1FAE5', border: '1px solid #6EE7B7', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#065F46' }}>✅ {statusMsg}</div>}
          {status === 'error'    && <div style={{ background: '#FEE2E2', border: '1px solid #FCA5A5', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem', color: '#991B1B' }}>❌ {statusMsg}</div>}

          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={handleUpload} disabled={status === 'uploading'}
              style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.8rem 1.6rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', opacity: status === 'uploading' ? 0.6 : 1 }}>
              {status === 'uploading' ? 'Processing...' : '➕ Add dishes'}
            </button>
            {status !== 'idle' && (
              <button onClick={reset} style={{ background: 'var(--warm)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.8rem 1.2rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                Reset
              </button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

// ── MAIN MENU PAGE ────────────────────────────────────────────────────────────
export default function Menu() {
  const { restaurant } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [editing, setEditing]       = useState(null);
  const [editForm, setEditForm]     = useState({});
  const [newCuisineTag, setNewCuisineTag] = useState('');
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [saving, setSaving]         = useState(false);
  const [photoUploading, setPhotoUploading] = useState(null);
  const photoInputRef  = useRef();
  const pendingDishRef = useRef(null);

  const load = async () => {
    if (!restaurant?.id) return;
    setLoading(true);
    const data = await portalFetch('/menu');
    if (data) setCategories(data.categories || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [restaurant?.id]);

  const handlePhotoClick = (dishId) => {
    pendingDishRef.current = dishId;
    photoInputRef.current?.click();
  };

  const handleDeletePhoto = async (dishId) => {
    if (!window.confirm('Remove this photo?')) return;
    setPhotoUploading(dishId);
    try {
      await portalFetch('/menu', {
        method: 'PATCH',
        body: JSON.stringify({ dishId, updates: { image_url: null, image_quality: null } }),
      });
      setCategories(prev => prev.map(cat => ({
        ...cat,
        dishes: (cat.dishes || []).map(d => d.id === dishId ? { ...d, image_url: null, image_quality: null } : d)
      })));
    } catch (err) {
      console.error('Delete photo failed:', err);
    } finally {
      setPhotoUploading(null);
    }
  };

  const handlePhotoFile = async (file) => {
    const dishId = pendingDishRef.current;
    if (!file || !dishId) return;
    if (!['image/jpeg', 'image/png', 'image/webp', 'image/avif'].includes(file.type)) return;
    if (file.size > MAX_FILE_SIZE) return;

    setPhotoUploading(dishId);
    try {
      const compressed  = await compressImage(file);
      const imageBase64 = await blobToBase64(compressed);
      const result      = await portalFetch('/upload-dish-image', {
        method: 'POST',
        body: JSON.stringify({ imageBase64, mimeType: 'image/jpeg', dishId }),
      });
      if (result?.url) {
        setCategories(prev => prev.map(cat => ({
          ...cat,
          dishes: (cat.dishes || []).map(d => d.id === dishId ? { ...d, image_url: result.url } : d)
        })));
      }
    } catch (err) {
      console.error('Photo upload failed:', err);
    } finally {
      setPhotoUploading(null);
      pendingDishRef.current = null;
    }
  };

  const startEdit = (dish) => {
    setEditing(dish.id);
    setEditForm({
      name: dish.name, description: dish.description || '', price: dish.price,
      is_active: dish.is_active, is_popular: dish.is_popular,
      category_id: dish.category_id || '',
      dietary_tags: dish.dietary_tags || [],
      cuisine_tags: dish.cuisine_tags || [],
      dish_role: dish.dish_role || 'meal',
    });
    setNewCuisineTag('');
  };

  const saveEdit = async (dishId) => {
    setSaving(true);
    await portalFetch('/menu', {
      method: 'PATCH',
      body: JSON.stringify({ dishId, updates: {
        name: editForm.name, description: editForm.description, price: parseFloat(editForm.price),
        is_active: editForm.is_active, is_popular: editForm.is_popular,
        category_id: editForm.category_id || null,
        dietary_tags: editForm.dietary_tags || [],
        cuisine_tags: editForm.cuisine_tags || [],
        dish_role: editForm.dish_role || null,
      } }),
    });
    setEditing(null);
    setSaving(false);
    await load();
  };
  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) return;
    setCreatingCategory(true);
    const result = await portalFetch('/categories', {
      method: 'POST',
      body: JSON.stringify({ display_name: newCategoryName.trim() }),
    });
    setCreatingCategory(false);
    if (result?.category) {
      setCategories(prev => [...prev, { ...result.category, dishes: [] }]);
      setEditForm(p => ({ ...p, category_id: result.category.id }));
      setShowNewCategoryInput(false);
      setNewCategoryName('');
    }
  };

  const toggleActive = async (dish) => {
    await portalFetch('/menu', {
      method: 'PATCH',
      body: JSON.stringify({ dishId: dish.id, updates: { is_active: !dish.is_active } }),
    });
    await load();
  };

  const [deletingDish, setDeletingDish] = useState(null);
  const [editingModifiersDish, setEditingModifiersDish] = useState(null);

  const deleteDish = async (dish) => {
    if (!window.confirm(`Permanently delete "${dish.name}"? This cannot be undone. Past orders and reviews will keep the dish name on record, but it will be removed from your menu entirely.`)) return;
    setDeletingDish(dish.id);
    try {
      await portalFetch('/menu', {
        method: 'DELETE',
        body: JSON.stringify({ dishId: dish.id }),
      });
      await load();
    } finally {
      setDeletingDish(null);
    }
  };

  const [syncing, setSyncing]   = useState(false);
  const [syncMsg, setSyncMsg]   = useState('');

  const syncFromPOS = async () => {
    setSyncing(true);
    setSyncMsg('');
    try {
      const result = await portalFetch('/sync-menu', { method: 'POST' });
      if (result?.ok) {
        setSyncMsg(`✓ Synced ${result.dishesUpserted} dishes, ${result.modGroupsUpserted} modifier groups`);
        await load();
      } else {
        setSyncMsg(result?.reason || result?.error || 'Sync failed');
      }
    } catch (err) {
      setSyncMsg('Sync failed — check console');
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(''), 5000);
    }
  };
  const totalDishes  = categories.reduce((s, c) => s + (c.dishes || []).length, 0);
  const activeDishes = categories.reduce((s, c) => s + (c.dishes || []).filter(d => d.is_active).length, 0);

  return (
    <div style={{ maxWidth: '100%', overflowX: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '1.8rem', fontWeight: 900 }}>Menu</h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.875rem', marginTop: 2 }}>
            {activeDishes} of {totalDishes} dishes active · {categories.length} categories
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {syncMsg && (
            <span style={{ fontSize: '0.8rem', color: syncMsg.startsWith('✓') ? '#065F46' : '#991B1B' }}>
              {syncMsg}
            </span>
          )}
          {restaurant?.pos_source && (
            <button onClick={syncFromPOS} disabled={syncing} style={{ background: syncing ? 'var(--border)' : '#E6F1FB', border: '1px solid #B5D4F4', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 600, fontSize: '0.875rem', cursor: syncing ? 'default' : 'pointer', color: '#0C447C' }}>
              {syncing ? 'Syncing...' : `🔄 Sync from ${restaurant.pos_source === 'square' ? 'Square' : 'Clover'}`}
            </button>
          )}
          <button onClick={load} style={{ background: 'var(--warm)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 1.2rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', color: 'var(--ink)' }}>
            Refresh
          </button>
        </div>
      </div>

      {/* Hidden photo file input */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/avif"
        style={{ display: 'none' }}
        onChange={e => { if (e.target.files[0]) handlePhotoFile(e.target.files[0]); e.target.value = ''; }}
      />

      <DeleteMenuCard onDeleted={() => { load(); }} />
      <AddMenuCard onSuccess={() => { load(); }} />

      {/* Menu list */}
      {loading ? (
        <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--muted)' }}>Loading menu...</div>
      ) : categories.length === 0 ? (
        <Card>
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>🍽️</div>
            <p style={{ color: 'var(--muted)', fontSize: '0.875rem' }}>No menu yet. Upload your menu above to get started.</p>
          </div>
        </Card>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {categories.map(cat => (
            <Card key={cat.id}>
              <div style={{ padding: '1rem 1.4rem', borderBottom: '1px solid var(--border)', background: 'var(--warm)', borderRadius: '12px 12px 0 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontWeight: 700, fontSize: '0.95rem' }}>{cat.display_name}</div>
                <span style={{ fontSize: '0.75rem', color: 'var(--muted)' }}>{(cat.dishes || []).length} dishes</span>
              </div>
              {(cat.dishes || []).map(dish => (
                <div key={dish.id} style={{ borderBottom: '1px solid var(--warm)' }}>
                  {editing === dish.id ? (
                    <div style={{ padding: '1rem 1.4rem', background: 'var(--cream)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: '0.8rem', marginBottom: '0.8rem' }}>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Name</label>
                          <input style={inputStyle} value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))} />
                        </div>
                        <div>
                          <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Price ($)</label>
                          <input style={inputStyle} type="number" step="0.01" value={editForm.price} onChange={e => setEditForm(p => ({ ...p, price: e.target.value }))} />
                        </div>
                      </div>
                      <div style={{ marginBottom: '0.8rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Description</label>
                        <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 60 }} value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))} />
                      </div>
                      <div style={{ marginBottom: '0.8rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Category</label>
                        <select
                          style={inputStyle}
                          value={editForm.category_id || ''}
                          onChange={e => {
                            if (e.target.value === '__new__') {
                              setShowNewCategoryInput(true);
                            } else {
                              setEditForm(p => ({ ...p, category_id: e.target.value }));
                            }
                          }}
                        >
                          {categories.map(c => (
                            <option key={c.id} value={c.id}>{c.display_name}</option>
                          ))}
                          <option value="__new__">+ Add custom category</option>
                        </select>
                        {showNewCategoryInput && (
                          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                            <input
                              style={{ ...inputStyle, flex: 1 }}
                              placeholder="New category name..."
                              value={newCategoryName}
                              onChange={e => setNewCategoryName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreateCategory(); } }}
                            />
                            <button
                              type="button"
                              onClick={handleCreateCategory}
                              disabled={creatingCategory}
                              style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
                            >
                              {creatingCategory ? 'Creating...' : 'Create'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setShowNewCategoryInput(false); setNewCategoryName(''); }}
                              style={{ background: 'var(--warm)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                            >
                              Cancel
                            </button>
                          </div>
                        )}
                      </div>
                      <div style={{ marginBottom: '0.8rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.3rem' }}>Dish role</label>
                        <select style={inputStyle} value={editForm.dish_role || 'meal'} onChange={e => setEditForm(p => ({ ...p, dish_role: e.target.value }))}>
                          {DISH_ROLE_OPTIONS.map(role => (
                            <option key={role} value={role}>{role.charAt(0).toUpperCase() + role.slice(1)}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ marginBottom: '0.8rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Dietary tags</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
                          {DIETARY_TAG_OPTIONS.map(tag => (
                            <label key={tag} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={(editForm.dietary_tags || []).includes(tag)}
                                onChange={e => setEditForm(p => ({
                                  ...p,
                                  dietary_tags: e.target.checked
                                    ? [...(p.dietary_tags || []), tag]
                                    : (p.dietary_tags || []).filter(t => t !== tag)
                                }))}
                                style={{ accentColor: 'var(--orange)' }}
                              /> {tag}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div style={{ marginBottom: '0.8rem' }}>
                        <label style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: '0.4rem' }}>Cuisine tags</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.5rem' }}>
                          {Array.from(new Set([...CUISINE_TAG_OPTIONS, ...(editForm.cuisine_tags || [])])).map(tag => (
                            <label key={tag} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', cursor: 'pointer' }}>
                              <input
                                type="checkbox"
                                checked={(editForm.cuisine_tags || []).includes(tag)}
                                onChange={e => setEditForm(p => ({
                                  ...p,
                                  cuisine_tags: e.target.checked
                                    ? [...(p.cuisine_tags || []), tag]
                                    : (p.cuisine_tags || []).filter(t => t !== tag)
                                }))}
                                style={{ accentColor: 'var(--orange)' }}
                              /> {tag}
                            </label>
                          ))}
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <input
                            style={{ ...inputStyle, flex: 1 }}
                            placeholder="Add a new cuisine tag..."
                            value={newCuisineTag}
                            onChange={e => setNewCuisineTag(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && newCuisineTag.trim()) {
                                e.preventDefault();
                                setEditForm(p => ({ ...p, cuisine_tags: [...(p.cuisine_tags || []), newCuisineTag.trim()] }));
                                setNewCuisineTag('');
                              }
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              if (newCuisineTag.trim()) {
                                setEditForm(p => ({ ...p, cuisine_tags: [...(p.cuisine_tags || []), newCuisineTag.trim()] }));
                                setNewCuisineTag('');
                              }
                            }}
                            style={{ background: 'var(--warm)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.8rem', cursor: 'pointer' }}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.8rem' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editForm.is_popular} onChange={e => setEditForm(p => ({ ...p, is_popular: e.target.checked }))} style={{ accentColor: 'var(--orange)' }} /> Mark as popular ⭐
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem', cursor: 'pointer' }}>
                          <input type="checkbox" checked={editForm.is_active} onChange={e => setEditForm(p => ({ ...p, is_active: e.target.checked }))} style={{ accentColor: 'var(--orange)' }} /> Active
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '0.8rem' }}>
                        <button onClick={() => saveEdit(dish.id)} disabled={saving} style={{ background: 'var(--orange)', color: 'white', border: 'none', borderRadius: 8, padding: '0.6rem 1.4rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer' }}>
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={() => setEditing(null)} style={{ background: 'var(--warm)', color: 'var(--ink)', border: '1px solid var(--border)', borderRadius: 8, padding: '0.6rem 1.4rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer' }}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div
                      style={{ display: 'flex', alignItems: 'center', padding: '0.85rem 1rem', gap: '0.75rem', opacity: dish.is_active ? 1 : 0.5, minWidth: 0 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--cream)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}
                    >
                      {photoUploading === dish.id ? (
                        <div style={{ width: 44, height: 44, background: 'var(--warm)', borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--orange)' }}>⏳</div>
                      ) : dish.image_url ? (
                        <div style={{ position: 'relative', flexShrink: 0 }}>
                          <img src={dish.image_url} alt={dish.name} style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', display: 'block' }} />
                          <div style={{ position: 'absolute', inset: 0, borderRadius: 8, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', opacity: 0, transition: 'opacity 0.15s' }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '0'}>
                            <button onClick={() => handlePhotoClick(dish.id)} title="Replace photo" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '2px', color: 'white' }}>✏️</button>
                            <button onClick={() => handleDeletePhoto(dish.id)} title="Delete photo" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.75rem', padding: '2px', color: '#FCA5A5' }}>🗑️</button>
                          </div>
                        </div>
                      ) : (
                        <div onClick={() => handlePhotoClick(dish.id)} title="Click to add photo"
                          style={{ width: 44, height: 44, background: 'var(--warm)', borderRadius: 8, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', color: 'var(--orange)', cursor: 'pointer', border: '1px dashed var(--border)', fontWeight: 600 }}>
                          +📷
                        </div>
                      )}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {dish.name}
                          {dish.is_popular && <span>⭐</span>}
                          {!dish.is_active && <span style={{ fontSize: '0.7rem', background: 'var(--border)', color: 'var(--muted)', padding: '0.1rem 0.4rem', borderRadius: 10 }}>Hidden</span>}
                        </div>
                        {dish.description && <div style={{ fontSize: '0.78rem', color: 'var(--muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{dish.description}</div>}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--orange)', flexShrink: 0 }}>{fmtMoney(dish.price)}</div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                        <button onClick={() => startEdit(dish)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: 'var(--orange)' }}>Edit</button>
                        <button onClick={() => setEditingModifiersDish(dish)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: 'var(--ink)' }}>Modifiers</button>
                        <button onClick={() => toggleActive(dish)} style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', color: dish.is_active ? 'var(--red-tx)' : 'var(--green-tx)' }}>
                          {dish.is_active ? 'Hide' : 'Show'}
                        </button>
                        <button onClick={() => deleteDish(dish)} disabled={deletingDish === dish.id} title="Permanently delete this dish"
                          style={{ background: 'none', border: '1px solid #FCA5A5', borderRadius: 6, padding: '0.35rem 0.8rem', fontSize: '0.8rem', fontWeight: 600, cursor: deletingDish === dish.id ? 'default' : 'pointer', color: '#991B1B', opacity: deletingDish === dish.id ? 0.6 : 1 }}>
                          {deletingDish === dish.id ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </Card>
          ))}
        </div>
      )}

      {/* Square catalog mapping — shown when dishes exist */}
      {categories.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <SquareCatalogMap
            categories={categories}
            restaurant={restaurant}
          />
        </div>
      )}

      {/* Clover inventory mapping — shown when dishes exist */}
      {categories.length > 0 && (
        <div style={{ marginTop: '1rem' }}>
          <CloverCatalogMap
            categories={categories}
            restaurant={restaurant}
          />
        </div>
      )}
      {editingModifiersDish && (
        <ModifierEditModal
          dish={editingModifiersDish}
          onClose={() => setEditingModifiersDish(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}
